import type { EWidgetSection } from '#shared';
import type { WidgetType } from './widgetRegistry';

/**
 * Maximum number of instances allowed per widget type in custom templates
 * If a widget type is not listed here, the default limit is 1
 */
export const WIDGET_LIMITS: Partial<Record<WidgetType, number>> = {
  'active-cases': 2,
  'new-cases': 1, // Only 1 NewCases widget (either standalone or in GeoMap)
  // geo-map has special handling - 1 in center, unlimited in sides
  // All other widgets default to 1
};

/**
 * Section restrictions for widget types
 * Defines which sections a widget can be placed in
 */
export const WIDGET_SECTION_RESTRICTIONS: Partial<
  Record<WidgetType, { allowed: EWidgetSection[]; limitPerSection?: number }>
> = {
  // geo-map can go anywhere, but only 1 in center, unlimited in sides
  'geo-map': {
    allowed: [
      'left' as EWidgetSection,
      'center' as EWidgetSection,
      'right' as EWidgetSection,
    ],
    limitPerSection: 1, // Only 1 geo-map per section (so max 1 in center)
  },
};

/**
 * Get the maximum allowed instances for a widget type
 */
export const getWidgetLimit = (widgetType: WidgetType): number =>
  WIDGET_LIMITS[widgetType] ?? 1;

/**
 * Check if adding another instance of this widget type would exceed the limit
 * For geo-map, we need special handling based on section
 */
export const canAddWidget = (
  widgetType: WidgetType,
  currentCount: number,
  targetSection?: EWidgetSection,
  currentCountInSection?: number,
): boolean => {
  // Special handling for geo-map
  if (widgetType === 'geo-map' && targetSection) {
    const restrictions = WIDGET_SECTION_RESTRICTIONS[widgetType];
    if (restrictions?.limitPerSection !== undefined) {
      return (currentCountInSection ?? 0) < restrictions.limitPerSection;
    }
  }

  const limit = getWidgetLimit(widgetType);
  return currentCount < limit;
};
